import { useEffect, useRef, useState } from "react";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import Select from "../../../../components/input/Select";
import { SimpleOption } from "../../../../components/input/SimpleListbox";
import { tooltipMark, formatNumberWithCommas } from "../../shared/reportUtil";
import { ReportLevel } from "../adminReportsApiSlice";
import { barAndLineChartColors } from "../adminReportsUtil";

const seriesTypes = ["Number of Essays", "Number of Drafts", "Avg Drafts Per Essay"];

const seriesOptions: SimpleOption[] = [
	{ value: "all", label: "All Analytics" },
	{ value: seriesTypes[0], label: seriesTypes[0] },
	{ value: seriesTypes[1], label: seriesTypes[1] },
	{ value: seriesTypes[2], label: seriesTypes[2] },
];

// Required properties for the chart
interface UsageDataRequiredBase {
	essayCount: number;
	draftCount: number;
	averageNumberOfDraftsPerEssay: number;
}

interface UsageChartProps<T extends UsageDataRequiredBase> {
	level: ReportLevel;
	data: T[];
	hasData: boolean;
	title: string;
	yAxisDataField: keyof T; // Property name to use for y-axis labels
	isEssayAndDraftSummaryChart: boolean;
}

export function EssayAndDraftCountUsageChart<T extends UsageDataRequiredBase>(
	props: UsageChartProps<T>
) {
	const [focusedSeries, setFocusedSeries] = useState("all");

	const chartRef = useRef<ReactEcharts>(null);
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	let chartColors = chartPalette;
	if (!chartPalette) {
		chartColors = barAndLineChartColors;
	}

	const maxAverage = Math.ceil(
		Math.max(...props.data.map((row) => row.averageNumberOfDraftsPerEssay))
	);

	const getDisplayedSeries = () => {
		if (focusedSeries === "all") {
			return {
				[seriesTypes[0]]: true,
				[seriesTypes[1]]: true,
				[seriesTypes[2]]: true,
			};
		}

		// Only set the selected series from the dropdown to true, hide the rest
		return {
			[seriesTypes[0]]: focusedSeries === seriesTypes[0],
			[seriesTypes[1]]: focusedSeries === seriesTypes[1],
			[seriesTypes[2]]: focusedSeries === seriesTypes[2],
		};
	};

	const initialEChartOptions: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		title: {
			text: props.title,
			left: "center",
			textStyle: { fontSize: 16, fontWeight: 600, color: foregroundColor },
		},
		legend: {
			selected: getDisplayedSeries(),
			selectedMode: false,
			top: 35,
			textStyle: {
				color: foregroundColor,
			},
		},
		grid: {
			containLabel: true,
			left: props.isEssayAndDraftSummaryChart ? 10 : 20,
			right: 70,
			bottom: 10,
			top: 75,
		},
		xAxis: [
			{
				// Axis for the number of drafts and essays bars
				type: "value",
				show: true,
				name: focusedSeries !== seriesTypes[2] ? "Counts" : "",
				position: "bottom",
				min: 0,
				minInterval: 1,
				max: focusedSeries === seriesTypes[2] ? 5 : undefined,
				alignTicks: true,
				axisLine: {
					show: true,
					lineStyle: {
						color: foregroundColor,
						width: 1,
					},
				},
				axisLabel: {
					show: focusedSeries !== seriesTypes[2],
				},
				splitLine: {
					show: true,
					lineStyle: {
						color: accentColor,
					},
				},
			},
			{
				// Axis for the average number of drafts per essay
				type: "value",
				show: focusedSeries === "all" || focusedSeries === seriesTypes[2],
				name: "Average",
				position: "top",
				min: 0,
				max:
					focusedSeries === seriesTypes[2]
						? undefined
						: props.isEssayAndDraftSummaryChart
							? maxAverage * 6
							: maxAverage * 4,
				minInterval: 1,
				alignTicks: true,
				axisLine: {
					show: true,
					lineStyle: {
						width: 2,
						color: chartColors[2],
					},
				},
				splitLine: {
					show: true,
					lineStyle: {
						color: accentColor,
					},
				},
				axisLabel: {
					color: chartColors[2],
					formatter: function (value: any) {
						return Math.round(value);
					},
				},
				nameTextStyle: {
					color: chartColors[2],
				},
			},
		],
		yAxis: {
			type: "category",
			data: props.data.map((row) => row[props.yAxisDataField]),
			inverse: true, // Forces the order of the rows in the table on initial load
			axisPointer: { type: "shadow" },
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			axisLabel: {
				padding: 10,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: {
					axisValue: string;
					dataIndex: number;
					color: string;
					seriesName: string;
					value: string;
				}[]
			) => {
				if (!params.find((p) => p.value)) return;

				const label = `<div>${props.data[params[0].dataIndex][props.yAxisDataField]}</div>`;
				const lineItem = (p: any) => {
					return `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${formatNumberWithCommas(Number(p.value))}</b></div>`;
				};
				const content = params.reduce((a: string, v: any) => a + lineItem(v), label);

				return content;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [],
	};

	useEffect(() => {
		const options = updateOptions(
			initialEChartOptions,
			buildSeriesOptions(props.data, foregroundColor, chartColors)
		);

		if (chartPalette != undefined) {
			options.color = chartPalette;
		}

		chartRef.current?.getEchartsInstance().setOption(options, true);
	}, [props.data, chartPalette, foregroundColor, accentColor, focusedSeries]);

	const chartHeight = props.data.length * 55 + 150;

	return (
		<>
			{props.hasData && (
				<div className="relative w-full mt-4 p-4 border-2 border-gray-200">
					<div className="z-10 absolute top-4 left-4">
						<Select
							simpleListBoxId="series-filter"
							value={focusedSeries}
							setValue={(value) => setFocusedSeries(value as string)}
							options={seriesOptions}
							fieldClasses="flex items-center gap-3"
							simpleListBoxClasses="w-64"
						/>
					</div>

					<ReactEcharts
						ref={chartRef}
						option={initialEChartOptions}
						className="w-full !min-h-96"
						style={{
							height: chartHeight,
						}}
					/>
				</div>
			)}
		</>
	);
}

function buildSeriesOptions<T extends UsageDataRequiredBase>(
	data: T[],
	foregroundColor: string,
	chartColors: string[]
): EChartsOption["series"] {
	return [
		{
			name: seriesTypes[0],
			type: "bar",
			cursor: "default",
			xAxisIndex: 0, // Maps to bottom axis
			data: data.map((row) => row.essayCount),
			itemStyle: { color: chartColors[0] },
			barMinWidth: 15,
			emphasis: {
				itemStyle: {
					shadowBlur: 7,
				},
			},
			label: {
				show: true,
				position: "right",
				fontWeight: "bold",
				color: foregroundColor,
				formatter: (params: any) => {
					return formatNumberWithCommas(params.value);
				},
			},
		},
		{
			name: seriesTypes[1],
			type: "bar",
			cursor: "default",
			xAxisIndex: 0, // Maps to bottom axis
			data: data.map((row) => row.draftCount),
			itemStyle: { color: chartColors[1] },
			barMinWidth: 15,
			emphasis: {
				itemStyle: {
					shadowBlur: 7,
				},
			},
			label: {
				show: true,
				position: "right",
				fontWeight: "bold",
				color: foregroundColor,
				formatter: (params: any) => {
					return formatNumberWithCommas(params.value);
				},
			},
		},
		{
			name: seriesTypes[2],
			type: "line",
			cursor: "default",
			connectNulls: true,
			xAxisIndex: 1, // Maps to top axis
			data: data.map((row) => row.averageNumberOfDraftsPerEssay),
			itemStyle: { color: chartColors[2] },
			lineStyle: { width: 3 },
			symbolSize: 10,
			emphasis: {
				itemStyle: {
					shadowBlur: 7,
				},
			},
		},
	];
}

function updateOptions(options: EChartsOption, seriesData: EChartsOption["series"]): EChartsOption {
	options.series = seriesData;
	return options;
}
